import { CustomerDetails } from '@/types/customers';
import { SVLink, SVSummaryCard } from '../core';
import { SVSummaryCardItem } from '@/types/common';
import { formatPhoneNumber } from '@/utils/phoneNumber';
import { ROUTES } from '@/constants/routes';
import StatusLabel from '../StatusLabel';
import { formatDateTimeFromUTC } from '@/utils/date';
import { buildAddress } from '@/utils/address';
import { Stack } from '@mui/material';
import { FORM_FIELDS } from '@/constants/formFields';
import TeammateLink from '../TeammateLink';
import SVHiddenText from '../core/SVHiddenText';
import { titleCase } from '@/utils/stringCaser';

type CustomerDetailsInfoProps = {
  customerDetails: CustomerDetails;
};

export default function CustomerDetailsInfo({
  customerDetails,
}: CustomerDetailsInfoProps) {
  const {
    firstName,
    middleName,
    lastName,
    email,
    phoneNumber,
    company,
    status,
    createdAt,
    deactivatedAt,
    advisors,
    ssn,
    dateOfBirth,
    lastLogin,
    kycStatus,
    address,
    mailingAddress,
  } = customerDetails;

  const items: SVSummaryCardItem[] = [
    {
      label: FORM_FIELDS.FIRST_NAME.label,
      text: firstName,
    },
    {
      label: FORM_FIELDS.MIDDLE_NAME.label,
      text: middleName,
    },
    {
      label: FORM_FIELDS.LAST_NAME.label,
      text: lastName,
    },
    {
      label: FORM_FIELDS.SSN.label,
      customContent: ssn ? (
        <SVHiddenText text={ssn} visibleCount={4} ariaLabel="SSN" />
      ) : null,
    },
    {
      label: FORM_FIELDS.EMAIL.label,
      text: email,
    },
    {
      label: FORM_FIELDS.PHONE.label,
      text: formatPhoneNumber(phoneNumber),
    },
    {
      label: FORM_FIELDS.DOB.label,
      text: formatDateTimeFromUTC(dateOfBirth),
    },
    {
      label: FORM_FIELDS.COMPANY.label,
      customContent: (
        <SVLink to={`${ROUTES.COMPANIES}/${company.uuid}`}>
          {company.name}
        </SVLink>
      ),
    },
    {
      label: 'Advisors',
      customContent: advisors.length ? (
        <Stack spacing={1}>
          {advisors.map(advisor => (
            <TeammateLink
              key={advisor.uuid}
              uuid={advisor.uuid}
              name={`${advisor.fullName}`}
            />
          ))}
        </Stack>
      ) : null,
    },
    {
      label: 'Status',
      customContent: <StatusLabel status={status} />,
    },
    {
      label: 'KYC Status',
      text: titleCase(kycStatus),
    },
    {
      label: 'Created Date',
      text: formatDateTimeFromUTC(createdAt),
    },
    {
      label: 'Deactivated Date',
      text: formatDateTimeFromUTC(deactivatedAt),
    },
    {
      label: 'Last Login',
      text: formatDateTimeFromUTC(lastLogin),
    },
    {
      label: 'Current Address',
      text: buildAddress(address),
    },
    {
      label: 'Mailing Address',
      text: buildAddress(mailingAddress),
    },
  ];

  return <SVSummaryCard label="Basic Info" items={items} />;
}
